import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StarSpinner } from "@/components/StarSpinner";

const ASSET_TYPES = [
  { value: "trademark", label: "Trademark" },
  { value: "copyright", label: "Copyright" },
  { value: "domain", label: "Domain" },
  { value: "patent", label: "Patent" },
  { value: "trade_secret", label: "Trade Secret" },
];

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "registered", label: "Registered" },
  { value: "renewal_due", label: "Renewal Due" },
  { value: "abandoned", label: "Abandoned" },
];

const EMPTY = {
  client_email: "",
  asset_type: "trademark",
  name: "",
  status: "pending",
  serial_number: "",
  registration_number: "",
  filing_date: "",
  registration_date: "",
  renewal_date: "",
  classification: "",
  jurisdiction: "USPTO",
  owner_of_record: "",
  notes: "",
};

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminIPAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // asset object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ip-assets");
      setAssets(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      client_email: a.client_email || "",
      asset_type: a.asset_type || "trademark",
      name: a.name || "",
      status: a.status || "pending",
      serial_number: a.serial_number || "",
      registration_number: a.registration_number || "",
      filing_date: a.filing_date || "",
      registration_date: a.registration_date || "",
      renewal_date: a.renewal_date || "",
      classification: a.classification || "",
      jurisdiction: a.jurisdiction || "",
      owner_of_record: a.owner_of_record || "",
      notes: a.notes || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_email || !form.name) {
      toast.error("Client email and asset name are required");
      return;
    }
    setSaving(true);
    try {
      // Strip empty strings so backend treats them as null / unset
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      if (editing) {
        await api.patch(`/admin/ip-assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/admin/ip-assets", payload);
        toast.success("Asset added");
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this IP asset? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/ip-assets/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="mt-10" data-testid="admin-ip-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-slate2 font-light">
          {assets.length} {assets.length === 1 ? "asset" : "assets"} under firm stewardship.
        </p>
        <button
          onClick={openNew}
          className="btn-brass"
          data-testid="admin-ip-add"
        >
          Add IP Asset
        </button>
      </div>

      {loading && (
        <div data-testid="admin-ip-loading"><StarSpinner label="Loading assets" /></div>
      )}

      {!loading && assets.length === 0 && (
        <div className="p-12 border border-transparent bg-linen/40 text-center font-serif italic text-slate2" data-testid="admin-ip-empty">
          No IP assets yet.
        </div>
      )}

      {!loading && assets.length > 0 && (
        <div className="bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-ip-list">
          <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
            <div className="col-span-3">Client</div>
            <div className="col-span-3">Asset</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Renewal</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {assets.map((a) => (
            <div
              key={a.id}
              className="bg-ivory grid grid-cols-12 gap-4 px-6 py-5 items-center"
              data-testid={`admin-ip-row-${a.id}`}
            >
              <div className="col-span-3 text-sm break-all">
                <div className="font-serif text-navy">{a.client_email}</div>
              </div>
              <div className="col-span-3">
                <div className="font-serif text-navy">{a.name}</div>
                <div className="text-xs text-slate2/70 uppercase tracking-[0.2em]">
                  {a.jurisdiction || "—"}
                  {a.registration_number ? ` · Reg ${a.registration_number}` : ""}
                </div>
              </div>
              <div className="col-span-2 font-serif italic text-slate2 text-sm">
                {a.asset_type.replace("_", " ")}
              </div>
              <div className="col-span-2 text-sm text-slate2 font-light">
                {fmtDate(a.renewal_date)}
              </div>
              <div className="col-span-1">
                <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-[0.22em] bg-oxblood/10 text-oxblood">
                  {a.status.replace("_", " ")}
                </span>
              </div>
              <div className="col-span-1 flex justify-end gap-3">
                <button
                  onClick={() => openEdit(a)}
                  className="text-slate2 hover:text-navy text-sm"
                  data-testid={`admin-ip-edit-${a.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(a.id)}
                  className="text-slate2/60 hover:text-oxblood text-sm"
                  data-testid={`admin-ip-delete-${a.id}`}
                  aria-label="Delete"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-navy/70 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
          data-testid="admin-ip-form-modal"
          onClick={closeForm}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory w-full max-w-3xl p-8 md:p-10 my-8 shadow-xl"
          >
            <div className="flex items-center justify-between hairline-bottom pb-4">
              <div>
                <div className="kicker">{editing ? "Edit Asset" : "New Asset"}</div>
                <h3 className="mt-2 font-serif text-2xl text-navy">
                  {editing ? "Update intellectual property record" : "Add to client portfolio"}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="text-slate2 text-sm"
                data-testid="admin-ip-form-close"
              >
                close ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mt-6">
              <Field label="Client Email" required>
                <input
                  type="email"
                  required
                  value={form.client_email}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  className="field"
                  data-testid="admin-ip-form-client-email"
                />
              </Field>
              <Field label="Asset Name" required>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="field"
                  data-testid="admin-ip-form-name"
                />
              </Field>

              <Field label="Asset Type">
                <select
                  value={form.asset_type}
                  onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                  className="field"
                  data-testid="admin-ip-form-type"
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="field"
                  data-testid="admin-ip-form-status"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Serial Number">
                <input
                  type="text"
                  value={form.serial_number}
                  onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                  className="field"
                />
              </Field>
              <Field label="Registration Number">
                <input
                  type="text"
                  value={form.registration_number}
                  onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                  className="field"
                />
              </Field>

              <Field label="Filing Date">
                <input
                  type="date"
                  value={form.filing_date}
                  onChange={(e) => setForm({ ...form, filing_date: e.target.value })}
                  className="field"
                />
              </Field>
              <Field label="Registration Date">
                <input
                  type="date"
                  value={form.registration_date}
                  onChange={(e) => setForm({ ...form, registration_date: e.target.value })}
                  className="field"
                />
              </Field>

              <Field label="Renewal Date">
                <input
                  type="date"
                  value={form.renewal_date}
                  onChange={(e) => setForm({ ...form, renewal_date: e.target.value })}
                  className="field"
                />
              </Field>
              <Field label="Jurisdiction">
                <input
                  type="text"
                  placeholder="USPTO, Texas SOS, WIPO…"
                  value={form.jurisdiction}
                  onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                  className="field"
                />
              </Field>

              <Field label="Classification" span={2}>
                <input
                  type="text"
                  placeholder="Nice Class 25, 41 · IC 009 · Category B"
                  value={form.classification}
                  onChange={(e) => setForm({ ...form, classification: e.target.value })}
                  className="field"
                />
              </Field>

              <Field label="Owner of Record" span={2}>
                <input
                  type="text"
                  placeholder="Individual or entity name that holds title"
                  value={form.owner_of_record}
                  onChange={(e) => setForm({ ...form, owner_of_record: e.target.value })}
                  className="field"
                  data-testid="admin-ip-form-owner"
                />
              </Field>

              <Field label="Notes" span={2}>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="field"
                />
              </Field>
            </div>

            <div className="mt-8 flex justify-end gap-3 hairline-top pt-6">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary disabled:opacity-70 inline-flex items-center gap-3"
                data-testid="admin-ip-form-submit"
              >
                {saving ? (
                  <>
                    <StarSpinner size="sm" label="" />
                    <span>Saving…</span>
                  </>
                ) : (
                  <span>{editing ? "Update Asset" : "Add Asset"}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required, span }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`}>
      <span className="kicker text-[0.62rem]">
        {label} {required && <span className="text-oxblood">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
